import { useState, useEffect, useCallback, useRef } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useContracts } from './useContract';

// Central data hook. Fetches everything the dashboard needs.
// Manual + interval refresh — NO event callbacks, so no render loops.
export function useUserData(pollMs = 20000) {
  const { account } = useWeb3();
  const { evittaRead, usdtRead } = useContracts();

  const [data, setData] = useState({
    loaded: false,
    registered: false,
    userId: 0,
    user: null,
    packages: [],
    income: null,
    eligibility: null,
    claimablePools: null,
    referrals: [],
    activities: [],
    usdtBalance: null,
    allowance: null,
    totalUsers: 0,
  });
  const [refreshing, setRefreshing] = useState(false);

  // Guard against overlapping fetches and stale setState after unmount.
  const busy = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!evittaRead) return;
    if (busy.current) return;
    busy.current = true;
    setRefreshing(true);
    try {
      const totalUsers = await evittaRead.totalUsers().catch(() => 0);

      let userId = 0;
      if (account) {
        userId = await evittaRead.addressToId(account).then((v) => Number(v)).catch(() => 0);
      }

      let usdtBalance = null;
      let allowance = null;
      if (account && usdtRead) {
        usdtBalance = await usdtRead.balanceOf(account).catch(() => null);
        allowance = await usdtRead
          .allowance(account, evittaRead.address)
          .catch(() => null);
      }

      if (!userId) {
        if (mounted.current) {
          setData((d) => ({
            ...d,
            loaded: true,
            registered: false,
            userId: 0,
            usdtBalance,
            allowance,
            totalUsers: Number(totalUsers),
          }));
        }
        return;
      }

      const [user, packages, income, eligibility, claimablePools, referrals, activities] =
        await Promise.all([
          evittaRead.users(userId).catch(() => null),
          evittaRead.getUserPackages(userId).catch(() => []),
          evittaRead.getUserIncomeBreakdown(userId).catch(() => null),
          evittaRead.getUserPoolEligibilities(userId).catch(() => null),
          evittaRead.getUserClaimablePoolIncomes(userId).catch(() => null),
          evittaRead.getUserReferrals(userId).catch(() => []),
          evittaRead.getUserActivities(userId).catch(() => []),
        ]);

      if (mounted.current) {
        setData({
          loaded: true,
          registered: true,
          userId,
          user,
          packages,
          income,
          eligibility,
          claimablePools,
          referrals,
          activities,
          usdtBalance,
          allowance,
          totalUsers: Number(totalUsers),
        });
      }
    } finally {
      busy.current = false;
      if (mounted.current) setRefreshing(false);
    }
  }, [evittaRead, usdtRead, account]);

  // Refetch when account or contracts change.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Light polling so balances/income stay fresh. Cleaned up properly.
  useEffect(() => {
    if (!pollMs) return undefined;
    const t = setInterval(() => {
      refresh();
    }, pollMs);
    return () => clearInterval(t);
  }, [refresh, pollMs]);

  return { ...data, refresh, refreshing };
}
